import React, { useMemo, useState, useEffect, useCallback } from "react";
import { errorTost, successTost } from '../../Services/Toast'
import {
  Box,
  Button,
  Container,
  Typography,
  Snackbar,
  Alert,
  Divider,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { ParkingHeader } from "./ParkingHeader";
import { WingAccordion } from "./WingAccordion";
import { SavedConfigurationCard } from "./SavedConfigurationCard";
import { ConfirmationDialog } from "./ConfirmationDialog";
import { EditConfigDialog } from "./EditConfigDialog"; 

import {
  fetchWingsFromProjectInventory,
  fetchUnitTypesFromProjectInventory,
  fetchProjectWingParkingTypesFromInventory,
  fetchParkingConfig,
  saveParkingConfig,
  updateParkingConfig, 
  deleteParkingConfig,
  updateProjectParkingToggle, fetchProjectParkingToggle, 
} from "../../Services/ParkingConfiguration";

export default function ParkingConfiguration({ projectId }) {
  const [isEnabled, setIsEnabled] = useState(true);
  const [unitConfigs, setUnitConfigs] = useState({});
  const [savedConfigs, setSavedConfigs] = useState([]);
  const [wings, setWings] = useState([]);
  const [loadingWings, setLoadingWings] = useState(false);
  const [parkingTypesByWing, setParkingTypesByWing] = useState({});
  const [loadingParkingTypesByWing, setLoadingParkingTypesByWing] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingConfigData, setEditingConfigData] = useState(null);
  const [selectedWingId, setSelectedWingId] = useState(null);
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });

 

  // ✅ HELPER: Check if ANY configs exist anywhere
  const hasAnyUnsavedConfigs = useCallback(() => {
    return Object.values(unitConfigs).some(configs => 
      Array.isArray(configs) && configs.length > 0
    );
  }, [unitConfigs]);

  // ✅ STRICT: Block other wings immediately
  const handleUnitConfigChange = useCallback((unitTypeId, wingId, configs) => {
    if (hasAnyUnsavedConfigs() && selectedWingId !== wingId) {
      errorTost(" Complete current wing first or save to configure another wing!", "error");
      return;
    }

    const safe = Array.isArray(configs)
      ? configs.filter(
          (c) =>
            c &&
            c.parkingTypeId &&
            (c.isUpgrade !== undefined || c.isUpgradeEligible !== undefined)
        )
      : [];
    
    const configsWithWing = safe.map(config => ({
      ...config,
      wingId
    }));
    
    setUnitConfigs((prev) => ({ 
      ...prev, 
      [unitTypeId]: configsWithWing 
    }));

    if (safe.length > 0 && !selectedWingId) {
      setSelectedWingId(wingId);
      successTost(`Configuring Wing ${wings.find(w => w.id === wingId)?.name}`, "success");
    }
    
    console.log('🔧 CONFIG WITH WING:', unitTypeId, wingId, configsWithWing);
  }, [selectedWingId, hasAnyUnsavedConfigs, wings]);
// ✅ SUPERCHARGED useEffect - Load EVERYTHING immediately
useEffect(() => {
  if (!projectId) return;

  const loadInitialData = async () => {
    setLoadingWings(true);

    // 1. Load ALL wings
    const { data: wingsData } = await fetchWingsFromProjectInventory(projectId);
    if (wingsData) {
      const wings = wingsData.map((w) => ({
        id: w.wingId,
        name: w.wingName,
        buildingName: "",
        unitTypes: [], // Will load per wing
      }));
      setWings(wings);
    }

    // 2. ✅ LOAD ALL SAVED CONFIGS IMMEDIATELY
    const { data: savedData } = await fetchParkingConfig(projectId);
    if (savedData?.length) {
      setSavedConfigs(
        savedData.map((c) => ({
          id: c.UnitType_ParkingType_Config_id,
          wingId: c.WingId,
          unitTypeId: c.UnitTypeId,
          parkingTypeId: c.ParkingTypeId,
          isUpgrade: c.isUpgrade,
          isUpgradeEligible: c.IsUpgradeEligible,
        }))
      );
      console.log('✅ LOADED', savedData.length, 'SAVED CONFIGS FOR ALL WINGS');
    }

    setLoadingWings(false);
  };

  loadInitialData();
}, [projectId]);





useEffect(() => {
  if (!projectId) return;

  const loadParkingToggle = async () => {
    const { data, error } = await fetchProjectParkingToggle(projectId);
    if (!error && data) {
      setIsEnabled(!!data.allowParkingSelection); // default false handled
    }
  };

  loadParkingToggle();
}, [projectId]);

// ✅ NEW: Auto-expand ALL wings with saved configs on load
useEffect(() => {
  if (savedConfigs.length > 0 && wings.length > 0) {
    wings.forEach(wing => {
      const hasSaved = savedConfigs.some(c => c.wingId === wing.id);
      if (hasSaved) {
        fetchUnitTypesForWing(wing.id);
        fetchParkingTypesForWing(wing.id);
      }
    });
  }
}, [savedConfigs, wings]);


  const handleEditConfig = useCallback((config) => {
    console.log('✏️ OPEN EDIT DIALOG:', config);
    setEditingConfigData(config);
    setEditDialogOpen(true);
  }, []);

  const handleSaveEditedConfig = async (updatedConfig) => {
    try {
      const fullConfig = {
        ProjectId: projectId,
        WingId: updatedConfig.wingId,
        UnitTypeId: updatedConfig.unitTypeId,
        ParkingTypeId: updatedConfig.parkingTypeId,
        isUpgrade: updatedConfig.isUpgrade || false,
        IsUpgradeEligible: updatedConfig.IsUpgradeEligible || false,
      };
      
      console.log('✏️ EDIT FULL CONFIG:', fullConfig);
      
      const { error } = await updateParkingConfig(fullConfig);
      if (error) throw error;
      
      await new Promise(r => setTimeout(r, 100));
      const { data: refreshed } = await fetchParkingConfig(projectId);
      setSavedConfigs(
        refreshed?.map((c) => ({
          id: c.UnitType_ParkingType_Config_id,
          wingId: c.WingId,
          unitTypeId: c.UnitTypeId,
          parkingTypeId: c.ParkingTypeId,
          isUpgrade: c.isUpgrade,
          isUpgradeEligible: c.IsUpgradeEligible,
        })) || []
      );
      
      successTost("Config updated!");
    } catch (error) {
      console.error('EDIT ERROR:', error);
      errorTost(`Update failed: ${error.message}`, "error");
    } finally {
      setEditDialogOpen(false);
      setEditingConfigData(null);
    }
  };

  const fetchParkingTypesForWing = async (wingId) => {
    if (parkingTypesByWing[wingId]) return;
    setLoadingParkingTypesByWing((prev) => ({ ...prev, [wingId]: true }));

    const { data } = await fetchProjectWingParkingTypesFromInventory(projectId, wingId);
    if (data?.length) {
      setParkingTypesByWing((prev) => ({
        ...prev,
        [wingId]: data.map((pt) => ({
          id: pt.parkingTypeId,
          name: pt.parkingType,
        })),
      }));
    }
    setLoadingParkingTypesByWing((prev) => ({ ...prev, [wingId]: false }));
  };

  const fetchUnitTypesForWing = async (wingId) => {
    const wing = wings.find((w) => w.id === wingId);
    if (!wing || wing.unitTypes.length > 0) return;

    const { data } = await fetchUnitTypesFromProjectInventory(projectId, wingId);
    if (data) {
      setWings((prev) =>
        prev.map((w) =>
          w.id === wingId
            ? {
                ...w,
                unitTypes: data.map((ut) => ({
                  id: ut.inventoryTypeId,
                  name: ut.displayUnitType,
                })),
              }
            : w
        )
      );
    }
  };

  const getParkingTypesForWing = useCallback((wingId) => parkingTypesByWing[wingId] || [], [parkingTypesByWing]);

  // ✅ NEW: Get wing-scoped unit configs
  const getWingScopedUnitConfigs = useCallback((wingId) => {
    return Object.fromEntries(
      Object.entries(unitConfigs).map(([unitTypeId, configs]) => [
        unitTypeId,
        Array.isArray(configs) ? configs.filter(c => c.wingId === wingId) : []
      ])
    );
  }, [unitConfigs]);

  const handleSaveConfiguration = async () => {
    if (saving) return;

    const wingIds = new Set();
    Object.values(unitConfigs).forEach(configs => {
      if (Array.isArray(configs)) {
        configs.forEach(config => {
          if (config.wingId) wingIds.add(config.wingId);
        });
      }
    });

    if (wingIds.size > 1) {
      errorTost("Only configurations from one wing can be saved together!", "error");
      return;
    }

    if (wingIds.size === 0) {
      errorTost("No valid configurations selected", "warning");
      return;
    }

    const validConfigs = [];
    Object.entries(unitConfigs).forEach(([unitTypeId, configs]) => {
      if (!configs?.length) return;
      
      configs.forEach((config) => {
        if (config.parkingTypeId && config.wingId) {
          validConfigs.push({
            ProjectId: projectId,
            WingId: config.wingId,
            UnitTypeId: unitTypeId,
            ParkingTypeId: config.parkingTypeId,
            isUpgrade: config.isUpgrade || false,
            IsUpgradeEligible: config.isUpgradeEligible || false,
          });
        }
      });
    });

    console.log('💾 INSERTING WITH WINGIDS:', validConfigs);

    try {
      setSaving(true);
      const { error } = await saveParkingConfig(validConfigs);
      if (error) throw error;

      await new Promise(r => setTimeout(r, 100));
      const { data: refreshed } = await fetchParkingConfig(projectId);
      setSavedConfigs(
        refreshed?.map((c) => ({
          id: c.UnitType_ParkingType_Config_id,
          wingId: c.WingId,
          unitTypeId: c.UnitTypeId,
          parkingTypeId: c.ParkingTypeId,
          isUpgrade: c.isUpgrade,
          isUpgradeEligible: c.IsUpgradeEligible,
        })) || []
      );
      setUnitConfigs({});
      setSelectedWingId(null);
      successTost(`Saved ${validConfigs.length} configs!`);
    } catch (error) {
      console.error('SAVE ERROR:', error);
      errorTost("This parking configuration already exists.", "warning");
    } finally {
      setSaving(false);
    }
  };



const handleParkingToggle = async (checked) => {
  // optimistic UI update
  setIsEnabled(checked);

  const { error } = await updateProjectParkingToggle(projectId, checked);

  if (error) {
    setIsEnabled(!checked);
    errorTost("Failed to update parking toggle");
    return;
  }

  checked
    ? successTost("Parking selection enabled")
    : successTost("Parking selection disabled");
};



  const handleRemoveConfig = async (configId) => {
    const config = savedConfigs.find((c) => c.id === configId);
    if (!config) return;

    try {
      const { data, error } = await deleteParkingConfig(
        projectId,
        config.wingId,
        config.unitTypeId,
        config.parkingTypeId
      );
      if (error) throw error;
      if (!data || data.length === 0) {
        errorTost("No matching config found", "warning");
        return;
      }

      setSavedConfigs((prev) => prev.filter((c) => c.id !== configId));
      successTost("Configuration deleted", "success");
    } catch (error) {
      console.error("DELETE ERROR:", error);
      errorTost("Delete failed", "error");
    }
    setDeleteConfirmId(null);
  };

  const unsavedCount = Object.values(unitConfigs).reduce(
    (total, configs) => total + (Array.isArray(configs) ? configs.length : 0),
    0
  );

  const groupedConfigs = useMemo(() => {
    return savedConfigs.reduce((acc, config) => {
      const wing = wings.find((w) => w.id === config.wingId);
      if (!wing || !wing.unitTypes?.length) return acc;
      if (!acc[wing.id]) acc[wing.id] = { wing, configs: [] };
      acc[wing.id].configs.push(config);
      return acc;
    }, {});
  }, [savedConfigs, wings]);

  return (
    <Box minHeight="100vh" bgcolor="background.default">
      <Container maxWidth="lg" sx={{ py: 4 }}>
       <ParkingHeader
  isEnabled={isEnabled}
  onToggle={handleParkingToggle}
/>
{!isEnabled && (
  <Alert severity="info" sx={{ mt: 2 }}>
    Parking configuration is disabled for this project.
    Enable it using the toggle above to configure parking.
  </Alert>
)}

        {isEnabled && (
          <>
            <Box mt={4}>
              <Typography sx={{ fontSize: 18, fontWeight: 700 }} mb={2}>
                Configure Parking by Wing
              </Typography>

              {loadingWings ? (
                <Typography color="text.secondary">Loading wings...</Typography>
              ) : wings.length === 0 ? (
                <Typography color="warning.main">No wings found</Typography>
              ) : (
                wings.map((wing) => (
                  <WingAccordion
                    key={wing.id}
                    wing={wing}
                    parkingTypes={getParkingTypesForWing(wing.id)}
                    loadingParkingTypes={loadingParkingTypesByWing[wing.id]}
                    // ✅ FIXED: Pass WING-SCOPED configs only
                    unitConfigs={getWingScopedUnitConfigs(wing.id)}
                    savedConfigs={savedConfigs.filter((c) => c.wingId === wing.id)}
                    selectedWingId={selectedWingId}
                    hasUnsavedConfigs={hasAnyUnsavedConfigs()}
                    onUnitConfigChange={(unitTypeId, configs) => 
                      handleUnitConfigChange(unitTypeId, wing.id, configs)
                    }
                    onExpand={async (wingId) => {
                      if (hasAnyUnsavedConfigs() && selectedWingId !== wingId) {
                        errorTost("Save or clear current wing configs first!", "warning");
                        return;
                      }
                      
                      if (selectedWingId && selectedWingId !== wingId) {
                        setUnitConfigs({});
                        
                      }
                      setSelectedWingId(wingId);
                      
                      await Promise.all([
                        fetchUnitTypesForWing(wingId),
                        fetchParkingTypesForWing(wingId)
                      ]);
                    }}
                  />
                ))
              )}
            </Box>

            <Box display="flex" justifyContent="flex-end" mt={3} gap={2} flexWrap="wrap">
              {hasAnyUnsavedConfigs() && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setUnitConfigs({});
                    setSelectedWingId(null);
                    successTost("🧹 Cleared all unsaved configs", "info");
                  }}
                >
                  Clear Unsaved
                </Button>
              )}
              
             
              <Button
                variant="contained"
                size="large"
                startIcon={<SaveIcon />}
                onClick={handleSaveConfiguration}
                disabled={!isEnabled || unsavedCount === 0 || saving}
                sx={{ fontSize: 15, px: 3, py: 1.2 }}
              >
                {saving ? "Saving..." : `Save Configs`}
              </Button>
            </Box>

            <Box mt={6}>
              <Typography sx={{ fontSize: 18, fontWeight: 700 }} mb={3}>
                Saved Configurations ({savedConfigs.length})
              </Typography>

              {Object.keys(groupedConfigs).length ? (
                Object.entries(groupedConfigs).map(([wingId, data]) => (
                  <Box
                    key={wingId}
                    mb={4}
                    p={3}
                    borderRadius={2}
                    border="1px solid"
                    borderColor="divider"
                    bgcolor="grey.50"
                  >
                    <Typography sx={{ fontSize: 16, fontWeight: 700 }} mb={2}>
                      {data.wing.buildingName || "Building"} — Wing {data.wing.name}
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    {data.configs.map((config) => (
                      <SavedConfigurationCard
                        key={config.id}
                        config={config}
                        wings={wings}
                        parkingTypes={getParkingTypesForWing(wingId)}
                        onRemove={(id) => setDeleteConfirmId(id)}
                        onEdit={handleEditConfig}
                      />
                    ))}
                  </Box>
                ))
              ) : (
                <Box
                  p={6}
                  border="1px dashed"
                  borderColor="divider"
                  textAlign="center"
                  borderRadius={2}
                  bgcolor="grey.50"
                >
                  <Typography sx={{ fontSize: 14 }} color="text.secondary">
                    No configurations saved yet. Configure above and save!
                  </Typography>
                </Box>
              )}
            </Box>
          </>
        )}

        <EditConfigDialog
          open={editDialogOpen}
          config={editingConfigData}
          wings={wings}
          parkingTypes={getParkingTypesForWing(editingConfigData?.wingId || '')}
          onClose={() => {
            setEditDialogOpen(false);
            setEditingConfigData(null);
          }}
          onSave={handleSaveEditedConfig}
        />

        <ConfirmationDialog 
          open={!!deleteConfirmId} 
          onClose={() => setDeleteConfirmId(null)}
          onConfirm={() => deleteConfirmId && handleRemoveConfig(deleteConfirmId)}
          title="Delete Configuration"
          description="Are you sure? This cannot be undone."
          confirmText="Delete"
          variant="destructive"
        />

        <Snackbar
          open={toast.open}
          autoHideDuration={4000}
          onClose={() => setToast((t) => ({ ...t, open: false }))}
        >
          <Alert severity={toast.severity} variant="filled">
            {toast.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
}
